import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import { SearchOutlined, CloseOutlined } from "uiCore";
import SbRefreshButton from "uiCore/SbRefreshButton/SbRefreshButton";
import SbButton from "uiCore/SbButton/SbButton";
import { SbSelect, FilterOutlined, SbTooltip } from "uiCore";
import SbTag from "uiCore/SbTag/SbTag";
import SbTable from "uiCore/SbTable/SbTable";

import { getJobRequisitionView } from "Api/apiService";
import useAuthStore from "Store/useAuthStore";
import { getJdStatusDisplay, getJdListProgress, dropArchivedJdFamilies, JD_LIST_STATUS_FILTERS, jdListStatusOnFilter } from "constants/jobs.constants";
import { usePendingJdRefresh } from "Hooks/useJdListRefresh";
import { buildFieldConfigMap } from "Utils/formatting";
import { formatTimestamp, toTimestampMs } from "Utils/timeFormat";

import {
  PageContainerStyled,
  PageHeaderStyled,
  HeaderLeftStyled,
  HeaderRightStyled,
  HeadingRowStyled,
  TableWrapperStyled,
} from "styles/common.styles";
import { SearchWrapperStyled, JobsTableWrapperStyled } from "Pages/JobsLibraryV2/JobsLibraryV2.styles";

const { Title, Text } = Typography;

const SEARCH_FIELDS = ["job_code", "sb_requisition_number", "job_title", "business_unit", "location", "function", "sub_function", "job_level"];

const SbAdminAllJobsView = ({ embedded = false, refreshTrigger = 0 }) => {
  const email = useAuthStore((s) => s.email);
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [jobData, setJobData] = useState([]);
  const [pageSearchTerm, setPageSearchTerm] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);

  const fieldMap = useMemo(
    () => buildFieldConfigMap(orgMetaData, 'req_no'),
    [orgMetaData],
  );
  const isSubFunctionVisible = fieldMap?.sub_function?.visible !== false;

  const fetchJobs = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await getJobRequisitionView(email);
      setJobData(dropArchivedJdFamilies(data) ?? []);
    } catch (err) {
      console.error("Error fetching all jobs:", err);
      // A failed quiet refresh keeps the current list.
      if (!silent) setJobData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [refreshTrigger]);

  usePendingJdRefresh(jobData, fetchJobs);

  useEffect(() => {
    if (!jobData.length) { setSearchOptions([]); return; }
    const unique = new Set();
    jobData.forEach((row) =>
      SEARCH_FIELDS.forEach((f) => {
        const v = row[f];
        if (v && typeof v === "string" && v.trim() !== "--") unique.add(v);
      })
    );
    setSearchOptions(Array.from(unique).map((v) => ({ value: v, label: v })));
  }, [jobData]);

  const filteredJobs = useMemo(() => {
    const term = (pageSearchTerm ?? "").trim().toLowerCase();
    if (!term) return jobData;
    return jobData.filter((row) =>
      SEARCH_FIELDS.some((f) => row[f]?.toString().toLowerCase().includes(term))
    );
  }, [jobData, pageSearchTerm]);

  const getJobTitle = (record) => record.job_title_for_jd || record.job_title || "";

  const columns = [
        {
      title: 'Job Code',
      dataIndex: 'job_code',
    },
    {
      title: fieldMap['job_code']?.label,
      dataIndex: 'sb_requisition_number',
    },
    {
      title: fieldMap['job_title']?.label,
      dataIndex: 'job_title',
      render: (_, record) => getJobTitle(record),
      // Filter on the displayed title (job_title_for_jd falls back to job_title).
      filters: [...new Set((filteredJobs || []).map(getJobTitle).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
        .map((value) => ({ text: value, value })),
      onFilter: (value, record) => getJobTitle(record) === value,
    },
    {
      title: fieldMap['business_unit']?.label,
      dataIndex: 'business_unit',
    },
    {
      title: fieldMap['location']?.label,
      dataIndex: 'location',
    },
    {
      title: fieldMap['function']?.label,
      dataIndex: 'function',
    },
    ...(isSubFunctionVisible
      ? [
        {
          title: fieldMap['sub_function']?.label,
          dataIndex: 'sub_function',
        },
      ]
      : []),
    {
      title: fieldMap['job_level']?.label,
      dataIndex: 'job_level',
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated_time_stamp',
      defaultSortOrder: 'descend',
      sorter: (a, b) => toTimestampMs(a.last_updated_time_stamp) - toTimestampMs(b.last_updated_time_stamp),
      render: (ts) => formatTimestamp(ts) || "—",
    },
    {
      title: 'Status',
      dataIndex: 'jd_status',
      filters: JD_LIST_STATUS_FILTERS,
      onFilter: jdListStatusOnFilter,
      render: (_, record) => {
        const { label, color, tooltip, style } = getJdStatusDisplay(record);
        return (
          <SbTooltip content={tooltip}>
            <SbTag color={color} style={style}>{label}</SbTag>
          </SbTooltip>
        );
      },
    },
  ];

  const getRowClassName = (record) => {
    const isJobApprovalPending =
      record.jd_status === "PENDING_APPROVAL" && record.assigned_approver_email === email;
    const isJobNeedRevision =
      record.jd_status === "NEEDS_REVISION" && record.user_id === email;

    if (isJobApprovalPending) return "jobs-table-content job-approval-pending";
    if (isJobNeedRevision) return "jobs-table-content needs-revision-jd-row";
    if (record.jd_status !== "PUBLISHED") return "jobs-table-content unpublished-jd-row";
    return "jobs-table-content";
  };

  const searchBar = (
    <SearchWrapperStyled>
      <SbSelect
        options={searchOptions}
        value={pageSearchTerm || undefined}
        suffixIcon={
          <SbButton buttonType="primary" size="small" icon={<SearchOutlined />} />
        }
        onChange={(v) => setPageSearchTerm(v ?? "")}
        placeholder="Search by job code, title…"
        allowClear
        showSearch
        notFoundContent="No matching results"
        listHeight={300}
      />
    </SearchWrapperStyled>
  );

  const table = (
    <JobsTableWrapperStyled>
      <SbTable
        columns={columns}
        dataSource={filteredJobs}
        rowKey={(r, idx) => r.sb_unique_id ?? idx}
        loading={loading}
        sticky
        rowClassName={(record) => {
          const progress = getJdListProgress(record);
          return `${getRowClassName(record)}${progress > 0 && progress < 100 ? " fill-bg" : ""}`;
        }}
        onRow={(record) => ({ style: { cursor: "default", "--progress": `${getJdListProgress(record)}%` } })}
        pagination={{
          showTotal: (total) => {
            const hasActiveFilters = total < (filteredJobs?.length ?? 0);
            return (
              <div className="paic-pagination-total">
                <FilterOutlined />
                <span className={hasActiveFilters ? "is-active-text" : "is-empty-text"}>
                  {hasActiveFilters
                    ? `${total} Jobs selected`
                    : "0 Jobs selected. Please select at least one role"}
                </span>
              </div>
            );
          },
        }}
      />
    </JobsTableWrapperStyled>
  );

  if (embedded) {
    return (
      <>
        <PageHeaderStyled>
          <HeaderLeftStyled>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Text type="secondary">View all jobs and their current status</Text>
              <SbRefreshButton onClick={fetchJobs} />
            </div>
          </HeaderLeftStyled>
          <HeaderRightStyled>{searchBar}</HeaderRightStyled>
        </PageHeaderStyled>
        <TableWrapperStyled>{table}</TableWrapperStyled>
      </>
    );
  }

  return (
    <PageContainerStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <HeadingRowStyled>
            <Title level={3}>All Jobs</Title>
          </HeadingRowStyled>
          <Text type="secondary">View all jobs and their current status</Text>
        </HeaderLeftStyled>

        <HeaderRightStyled>
          <SbButton
            buttonType="action"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => navigate('/profile', { replace: true })}
            title="Back to Manage Profiles"
          />
          {searchBar}
        </HeaderRightStyled>
      </PageHeaderStyled>

      <TableWrapperStyled>{table}</TableWrapperStyled>
    </PageContainerStyled>
  );
};

export default SbAdminAllJobsView;
